import React from "react";
import { ActionIcon, Table, ThemeIcon, Tooltip } from "@mantine/core";
import { IconInfoTriangle, IconSettings } from "@tabler/icons-react";
import { formatDate } from "@utils/CommonUtil";
import { Organization } from "types/orgTypes";
import { useTranslation } from "react-i18next";

interface OrganizationsTableProps {
  organizations: Organization[];
  showDetails: boolean;
  onOrganizationSelect?: (orgId: string) => void;
  onDistSelect?: (distId: string) => void;
  showCountry?: boolean;
  showDistId?: boolean;
  showScopeName?: boolean;
  dateLocale?: string;
  multiSelect?: boolean;
  selected?: string[];
  onSelectChange?: (selected: string[]) => void;
  rowOffset?: number;
}

const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
  organizations,
  showDetails,
  onOrganizationSelect,
  onDistSelect,
  showCountry = false,
  showDistId = false,
  showScopeName = false,
  multiSelect = false,
  selected = [],
  onSelectChange,
  rowOffset = 0,
}) => {
  const { t } = useTranslation();

  const handleCheckbox = (orgId: string) => {
    if (!onSelectChange) return;
    if (selected.includes(orgId)) {
      onSelectChange(selected.filter((id) => id !== orgId));
    } else {
      onSelectChange([...selected, orgId]);
    }
  };

  const allSelected =
    organizations.length > 0 && organizations.every((item) => selected.includes(item.orgId));
  const handleSelectAll = () => {
    if (!onSelectChange) return;
    if (allSelected) {
      onSelectChange([]);
    } else {
      onSelectChange(organizations.map((item) => item.orgId));
    }
  };

  return (
    <Table highlightOnHover striped verticalSpacing="xs">
      <Table.Thead>
        <Table.Tr>
          {multiSelect && (
            <Table.Th>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = selected.length > 0 && !allSelected;
                }}
                onChange={handleSelectAll}
              />
            </Table.Th>
          )}
          <Table.Th>No</Table.Th>
          <Table.Th>Date</Table.Th>
          <Table.Th>Name</Table.Th>
          <Table.Th>Address</Table.Th>
          {showCountry && <Table.Th>Country</Table.Th>}
          <Table.Th>Email</Table.Th>
          <Table.Th>Org Id</Table.Th>
          <Table.Th />
          {showDistId && <Table.Th>Dist Id</Table.Th>}
          {showScopeName && <Table.Th>Scope</Table.Th>}
          {showDetails ? <Table.Th>Products</Table.Th> : null}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {organizations.map((item, index) => (
          <Table.Tr key={item.orgId ?? index}>
            {multiSelect && (
              <Table.Td>
                <input
                  type="checkbox"
                  checked={selected.includes(item.orgId)}
                  onChange={() => handleCheckbox(item.orgId)}
                />
              </Table.Td>
            )}
            <Table.Td>{rowOffset + index + 1}</Table.Td>
            <Table.Td>{formatDate(item.creationDate)}</Table.Td>
            <Table.Td>{item.name}</Table.Td>
            <Table.Td>{item.address}</Table.Td>
            {showCountry && <Table.Td>{item.country}</Table.Td>}
            <Table.Td>{item.email}</Table.Td>
            <Table.Td>{item.orgId}</Table.Td>
            <Table.Td>
              <Tooltip.Group>
                {(!item.address ||
                  !item.email ||
                  !item.accountInfo ||
                  !item.accountType ||
                  item.accountType.length === 0) && (
                  <Tooltip label={t("distributorDashboard.missingFieldsTooltip")}>
                    <ThemeIcon
                      variant="light"
                      radius="lg"
                      color="red"
                      style={{ cursor: "pointer" }}
                    >
                      <IconInfoTriangle style={{ width: "70%", height: "70%" }} />
                    </ThemeIcon>
                  </Tooltip>
                )}
                {onOrganizationSelect && (
                  <Tooltip label={t("distributorDashboard.manageOrg")}>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => onOrganizationSelect(item.orgId)}
                      aria-label={t("distributorDashboard.manageOrg")}
                    >
                      <IconSettings style={{ width: "70%", height: "70%" }} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Tooltip.Group>
            </Table.Td>
            {showDistId && (
              <Table.Td
                style={{ cursor: onDistSelect ? "pointer" : undefined }}
                onClick={() => onDistSelect && item.distId && onDistSelect(item.distId)}
              >
                {item.distId}
              </Table.Td>
            )}
            {showScopeName && <Table.Td>{item.scopeName || "-"}</Table.Td>}
            {showDetails ? (
              <Table.Td>
                {item?.products?.map((product, i) => (
                  <React.Fragment key={i}>
                    {" "}
                    <p>{product?.serialNo}</p>
                    {showScopeName && <p>{product?.scopeName}</p>}
                  </React.Fragment>
                ))}
              </Table.Td>
            ) : null}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

export default OrganizationsTable;
